/**
 * 完善个人资料 `/profile/complete`（账号级资料强制填写页）
 * -----------------------------------------------------------------------------
 * 为什么要有这一页：个人中心原来从「最近一份报名单」的 `applicantName` 取姓名，
 * 代报会显示别人的名字、新建草稿会把它顶成空。改成账号级 `users.realName` 之后，
 * 账号第一次进来必须把它填上，否则个人中心没有可显示的真名。
 *
 * 口径：
 *   - **姓名 + 手机号都齐了才算完成**（`profileCompleted`，服务端派生）。
 *     手机号是必要条件，因为报名页的手机号字段要预填它、并按它走免验。
 *   - 手机号与报名页**共用同一套发码链路**（`sendPhoneCode` → 同一短信模板），
 *     不另开发送通道，也不另报模板。
 *   - 验证码**不在这里单独校验**：连同手机号一起交给 `PUT /profile/basic`，
 *     由服务端在同一处校验并落库，避免「本地验过了、提交时码已消费」的坑
 *     （`verifyCode` 成功即删码，先验后提交必然第二次失败）。
 *   - 手机号注册时**已绑定**的账号（存量改过号的人），此页只要求补姓名，
 *     号码一栏只读展示 —— 改号是个人中心的事，不在这条强制链路上做。
 *
 * 出口：页脚放「联系客服」。短信通道出问题（未开通 / 欠费 / 签名审核未过）时，
 * 学员会被这页挡住，必须留一条能联系到人的路，否则等于把人锁死在门外。
 *
 * 回跳：`?next=` 由 `RequireProfile` 带上，仅接受站内相对路径（`safeNextPath`）。
 *
 * ⚠️ 版式约定（2026-10-31 返工）：页内必须用 `Card` + `.field/.field__label/
 * `.field__control` 这套**行式字段**，不能用 `ListCard`。
 *   - `Card` 自带 16px 内边距；`ListCard` 的 padding 是 `12px 0 4px`（左右为 0，
 *     靠 `.row-item` 自带 16px 撑开），把输入框直接放进去会贴到卡片边框上；
 *   - `.field-box` 是**无边框透明底**的行式输入（设计稿靠「灰标签 + 深值 + 细分隔线」
 *     表达可编辑），单独使用时没有任何视觉边界，必须配 `.field__label` 与
 *     `.field + .field` 的分隔线才成立。
 * 这两个凑一起就是「输入框悬空、没有左右留白」的那种难看，别再改回去。
 */
import { useQueryClient } from '@tanstack/react-query';
import { Input, Toast } from 'antd-mobile';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { describeApiError } from '../api/errors';
import { saveProfileBasic, sendPhoneCode } from '../api/h5';
import { h5QueryKeys, useProfileBasic, useSession } from '../api/hooks';
import { CaptchaModal, type CaptchaSubmit } from '../components/CaptchaModal';
import { Button, Card, Notice, PageShell } from '../components/layout';
import { safeNextPath } from '../components/RequireProfile';
import { maskPhone } from '../lib/phone-format';
import { useSendCooldown } from '../lib/send-cooldown';

const MOBILE_PATTERN = /^1[3-9]\d{9}$/;
/** 与服务端 `PROFILE-001` 的判定保持一致（去首尾空白后 2~20 字） */
const NAME_MIN = 2;
const NAME_MAX = 20;

export default function ProfileComplete() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const session = useSession();
  const basic = useProfileBasic();
  const next = safeNextPath(params.get('next'));
  /**
   * 编辑模式（`?edit=1`，从个人中心「修改」进来）。
   *
   * 与强制填写模式共用一个页面，但**不能**共用「资料已完整就跳走」这条守卫 ——
   * 那会把点「修改」的人立刻弹回原页面，等于改不了。
   */
  const editMode = params.get('edit') === '1';

  const [realName, setRealName] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  /**
   * 验证码这一行只在**发过码之后**出现。
   * 与报名表单的 `PhoneVerifyField` 同一口径：没发码就摆一个空验证码框，
   * 看起来像「这里填错了」，而且会把「获取验证码」这个动作埋掉。
   */
  const [codeSent, setCodeSent] = useState(false);
  const [captchaVisible, setCaptchaVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const { remaining: cooldown, markSent } = useSendCooldown();

  /** 账号已有手机号 → 只补姓名，号码只读 */
  const boundMasked = basic.data?.phoneMasked ?? null;
  const needPhone = !boundMasked;
  const phoneOk = MOBILE_PATTERN.test(phone.trim());
  const nameOk = realName.trim().length >= NAME_MIN && realName.trim().length <= NAME_MAX;

  // 回显服务端已存的姓名（只填一次，不覆盖用户正在输入的内容）
  useEffect(() => {
    const saved = basic.data?.realName;
    if (saved) setRealName((current) => (current === '' ? saved : current));
  }, [basic.data?.realName]);

  // 资料已完整的账号不该再看到这一页（直接访问 / 返回键命中都会走到）。
  // 编辑模式例外：那是用户主动来改的。
  useEffect(() => {
    if (!editMode && session.data?.profileCompleted) navigate(next, { replace: true });
  }, [editMode, session.data?.profileCompleted, next, navigate]);

  /**
   * 发码。失败直接抛给浮层（它负责换图 + 提示），这里只管成功收尾 ——
   * 再 catch 一次就会出现两句提示。
   */
  const send = async ({ captchaId, captchaCode }: CaptchaSubmit) => {
    const result = await sendPhoneCode(phone.trim(), { captchaId, captchaCode });
    markSent(result.cooldownSeconds);
    setCodeSent(true);
    if (result.debugCode) {
      // 仅本地/测试：短信通道未配置时服务端回显验证码，省掉翻 Redis 取码
      setCode(result.debugCode);
      Toast.show({ icon: 'success', content: '测试环境已自动填入验证码' });
    } else {
      Toast.show({ icon: 'success', content: `验证码已发送至 ${maskPhone(phone.trim())}` });
    }
  };

  const submit = async () => {
    if (busy || !nameOk) return;
    if (needPhone && (!phoneOk || code.trim().length !== 6)) return;
    setBusy(true);
    try {
      await saveProfileBasic({
        realName: realName.trim(),
        ...(needPhone ? { phone: phone.trim(), code: code.trim() } : {}),
      });
      // 会话要重取（profileCompleted 变了），资料缓存也要重取，否则回跳后仍被拦
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: h5QueryKeys.session }),
        queryClient.invalidateQueries({ queryKey: h5QueryKeys.profileBasic }),
      ]);
      Toast.show({ icon: 'success', content: '资料已保存' });
      navigate(next, { replace: true });
    } catch (error) {
      Toast.show({ icon: 'fail', content: describeApiError(error) });
    } finally {
      setBusy(false);
    }
  };

  const canSubmit = nameOk && (!needPhone || (phoneOk && code.trim().length === 6));

  return (
    <PageShell
      title={editMode ? '修改资料' : '完善个人资料'}
      back={editMode}
      footer={
        <Button variant="primary" block loading={busy} disabled={!canSubmit} onClick={submit}>
          {editMode ? '保存' : '保存并继续'}
        </Button>
      }
    >
      <Notice tone="info">
        {editMode
          ? '可修改姓名；手机号如需更换，请到个人中心操作。'
          : '首次进入需填写真实姓名与手机号。手机号用于接收报名与教材相关通知，填写后可在个人中心修改。'}
      </Notice>

      <Card title="基本信息">
        <div className="field">
          <span className="field__label">
            姓名<em className="field__required">*</em>
          </span>
          <span className="field__control">
            <div className="field-box">
              <Input
                value={realName}
                maxLength={NAME_MAX}
                placeholder={`请输入真实姓名（${NAME_MIN}-${NAME_MAX} 字）`}
                onChange={setRealName}
              />
            </div>
            <span className="field__help">请填写与身份证一致的真实姓名</span>
          </span>
        </div>

        {needPhone ? (
          <div className="field">
            <span className="field__label">
              手机号<em className="field__required">*</em>
            </span>
            <span className="field__control">
              <div className="phone-verify">
                <div className="phone-verify-row phone-verify-row--between">
                  <div className="field-box phone-verify__input">
                    <Input
                      type="tel"
                      inputMode="numeric"
                      maxLength={11}
                      value={phone}
                      placeholder="请输入手机号"
                      onChange={setPhone}
                    />
                  </div>
                  <button
                    type="button"
                    className="link-text"
                    disabled={!phoneOk || cooldown > 0 || busy}
                    onClick={() => setCaptchaVisible(true)}
                  >
                    {cooldown > 0 ? `${cooldown}s 后重发` : codeSent ? '重新获取' : '获取验证码'}
                  </button>
                </div>

                {codeSent ? (
                  <div className="phone-verify-row">
                    <div className="field-box">
                      <Input
                        value={code}
                        inputMode="numeric"
                        maxLength={6}
                        placeholder="6 位验证码"
                        onChange={setCode}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
              <span className="field__help">验证码发送至该手机号，验证通过即完成绑定</span>
            </span>
          </div>
        ) : (
          <div className="field">
            <span className="field__label">手机号</span>
            <span className="field__control">
              <div className="field-box">
                <Input value={boundMasked ?? ''} readOnly />
              </div>
              <span className="field__help">如需更换手机号，请到个人中心修改</span>
            </span>
          </div>
        )}
      </Card>

      {/* 「收不到验证码」只在这一页真的要收码时才成立；编辑态只改姓名时留着是噪音 */}
      {needPhone ? (
        <p className="field-hint">
          <span>收不到验证码？可先</span>
          <button type="button" className="link-text" onClick={() => navigate('/help/service')}>
            联系客服
          </button>
        </p>
      ) : null}

      <CaptchaModal
        visible={captchaVisible}
        onClose={() => setCaptchaVisible(false)}
        onSubmit={send}
      />
    </PageShell>
  );
}
